import { useEffect, useState } from 'react';

interface CancelOrderModalProps {
  isOpen: boolean;
  orderId: string;
  submitting?: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void | Promise<void>;
}

export default function CancelOrderModal({
  isOpen,
  orderId,
  submitting = false,
  onCancel,
  onConfirm,
}: CancelOrderModalProps) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setReason('');
    setError('');
  }, [isOpen, orderId]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    const text = reason.trim();
    if (!text) {
      setError('请填写取消原因');
      return;
    }
    if (text.length > 255) {
      setError('取消原因最长不超过255个字符');
      return;
    }
    void onConfirm(text);
  };

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40"
      onClick={submitting ? undefined : onCancel}
    >
      <div
        className="bg-background-50 rounded-xl w-full max-w-md mx-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-background-200 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 bg-primary-100">
            <i className="ri-close-circle-line text-primary-600 text-lg" />
          </div>
          <h3 className="text-sm font-semibold text-foreground-900">取消订单</h3>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div className="bg-background-100 rounded-lg p-3">
            <p className="text-xs text-foreground-500">订单号</p>
            <p className="text-sm font-medium text-foreground-800 mt-0.5">{orderId}</p>
          </div>

          <p className="text-sm text-foreground-600 leading-relaxed">
            确认取消该订单？取消后无法恢复，请填写取消原因。
          </p>

          <div>
            <label className="text-sm font-medium text-foreground-800 mb-2 block">
              取消原因 <span className="text-primary-500">*</span>
            </label>
            <textarea
              value={reason}
              disabled={submitting}
              onChange={(e) => {
                setReason(e.target.value.slice(0, 255));
                if (error) setError('');
              }}
              rows={4}
              maxLength={255}
              placeholder="请填写取消订单的原因..."
              className={`w-full px-3 py-2.5 rounded-lg border text-sm outline-none resize-none transition-colors disabled:bg-background-100 ${
                error
                  ? 'border-primary-400 focus:border-primary-400'
                  : 'border-background-300 focus:border-primary-400'
              }`}
            />
            <div className="flex items-center justify-between mt-1.5 gap-2">
              {error ? (
                <p className="text-xs text-primary-600">{error}</p>
              ) : (
                <span />
              )}
              <p className="text-xs text-foreground-400 shrink-0">{reason.length}/255</p>
            </div>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-background-200 flex items-center justify-end gap-2">
          <button
            type="button"
            disabled={submitting}
            onClick={onCancel}
            className="px-4 py-2 rounded-lg border border-background-300 text-foreground-600 text-sm hover:bg-background-100 transition-colors whitespace-nowrap disabled:opacity-50"
          >
            返回
          </button>
          <button
            type="button"
            disabled={submitting}
            onClick={handleConfirm}
            className="px-4 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors whitespace-nowrap inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            {submitting && <i className="ri-loader-4-line animate-spin text-sm" />}
            {submitting ? '提交中...' : '确认取消'}
          </button>
        </div>
      </div>
    </div>
  );
}
